import React, { useCallback, memo, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import ContentViewSwitch from '../../../components/content-view-switch/ContentViewSwitch';
import { PROJECTS_LIST_SIZE_OPTIONS, SORT_ORDER } from '../../../constants';
import useProjectTypeOptions from '../../../hooks/useProjectTypeOptions';
import WithLabel, { LABEL_STYLES } from '../../../components/with-label/WithLabel';
import DropdownWithIcons from '../../../components/common-dropdown/DropdownWithIcons';
import SearchInput from '../../../components/common-inputs/SearchInput';
import SortDropdown from '../../../components/sort-dropdown/SortDropdown';
import CommonDropdown from '../../../components/common-dropdown/CommonDropdown';
import { getFilterUsers, loadMoreFilterUsers } from '../../../redux/projects/projectsActions';
import useFilterUserOptions from './useFilterUserOptions';
import {
  changeTableCreatedBy,
  changeTableProjectType,
  changeTableSearch,
  changeTableSize,
  changeTableSort,
} from '../../../redux/projects/projectsReducer';
import removeDash from '../../../utils/removeDash';
import useProjectsTableSort from './useProjectsTableSort';
import CommonVirtualizedDropdown from '../../../components/common-virtualized-dropdown/CommonVirtualizedDropdown';

import scss from '../ProjectsList.module.scss';

const ProjectsFilters = ({ onChangeContentView, contentView }) => {
  const { t } = useTranslation('projects');
  const dispatch = useDispatch();
  const {
    isFilterUsersLoading,
    totalFilterUsers,
    tableSettings: { createdBy, size, projectType, searchQuery, sort },
  } = useSelector((state) => state.projects);

  const filterUsersOptions = useFilterUserOptions();
  const projectTypeOptions = useProjectTypeOptions();
  const sortOptions = useProjectsTableSort();

  const typeOptions = useMemo(
    () => [
      {
        label: t('all'),
        value: '',
      },
      ...projectTypeOptions,
    ],
    [t, projectTypeOptions],
  );

  const filterUserOptions = useMemo(
    () => [
      {
        label: '',
        value: '',
      },
      ...filterUsersOptions,
    ],
    [filterUsersOptions],
  );

  const handleChangeProjectType = useCallback(
    (newProjectType) => {
      dispatch(changeTableProjectType(newProjectType));
    },
    [dispatch],
  );

  const handleChangeSearch = useCallback(
    (newSearch) => {
      dispatch(changeTableSearch(newSearch));
    },
    [dispatch],
  );

  const handleChangeTableSize = useCallback(
    (newSize) => {
      dispatch(changeTableSize(newSize));
    },
    [dispatch],
  );

  const handleSelectFilterUser = useCallback(
    (user) => {
      dispatch(changeTableCreatedBy(user));
    },
    [dispatch],
  );

  const handleSearchFilterUsers = useCallback(
    (searchStr) => {
      dispatch(getFilterUsers({ search: searchStr, page: 1 }));
    },
    [dispatch],
  );

  const handleLoadMoreFilterUsers = useCallback(
    ({ search, page }) => {
      dispatch(loadMoreFilterUsers({ search, page }));
    },
    [dispatch],
  );

  const handleChangeSort = useCallback(
    ({ sort, direction }) => {
      dispatch(changeTableSort(`${direction === SORT_ORDER.DESC ? '-' : ''}${sort}`));
    },
    [dispatch],
  );

  useEffect(() => {
    dispatch(getFilterUsers({ search: '', page: '' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={scss.filtersWrapper}>
      <div className={scss.section}>
        <WithLabel label={t('type')} variant={LABEL_STYLES.ROW} isBold maxWidth={250}>
          <DropdownWithIcons value={projectType} options={typeOptions} onSelect={handleChangeProjectType} />
        </WithLabel>

        <SearchInput value={searchQuery} onChange={handleChangeSearch} maxWidth={250} size='sm' tabIndex={'-1'} />
      </div>

      <div className={scss.section}>
        <WithLabel label={t('createdBy')} variant={LABEL_STYLES.ROW} isBold maxWidth={250}>
          <CommonVirtualizedDropdown
            onSearch={handleSearchFilterUsers}
            isLoading={isFilterUsersLoading}
            value={createdBy}
            onSelect={handleSelectFilterUser}
            totalElements={totalFilterUsers + 1}
            options={filterUserOptions}
            onLoadMore={handleLoadMoreFilterUsers}
            isHighlighted
          />
        </WithLabel>

        <WithLabel label={t('sortBy')} variant={LABEL_STYLES.ROW} isBold maxWidth={250}>
          <SortDropdown
            options={sortOptions}
            sort={removeDash(sort)}
            direction={sort.includes('-') ? SORT_ORDER.DESC : SORT_ORDER.ASC}
            onChange={handleChangeSort}
            closeOnApply
          />
        </WithLabel>

        <WithLabel label={t('show')} variant={LABEL_STYLES.ROW} isBold maxWidth={150}>
          <CommonDropdown options={PROJECTS_LIST_SIZE_OPTIONS} value={size} onSelect={handleChangeTableSize} />
        </WithLabel>

        <ContentViewSwitch onChangeContentView={onChangeContentView} contentView={contentView} />
      </div>
    </div>
  );
};

export default memo(ProjectsFilters);
